import { AuthPage } from "@tether-web-portal/components/auth/AuthPage";
import { useAuth } from "../lib/auth/useAuth";
import { lazy, Suspense, type FC } from "react";
import { Loader } from "@tether-web-portal/components/loading/Loader";
import { useLocation } from "react-router";
import { isOAuthConsentPreview } from "../lib/oauthConsentPreview";

const SsoCallbackPage = lazy(() => import("./SsoCallbackPage"));

export const AuthGuardPage: FC = ({ children }) => {
  const { user, isLoadingAuth } = useAuth();
  const location = useLocation();

  // SSO callback must be handled before any other auth check — the user is
  // not yet authenticated when Azure AD redirects back with the code.
  if (location.pathname === "/auth/sso/callback") {
    return (
      <Suspense fallback={<Loader height="100vh" />}>
        <SsoCallbackPage />
      </Suspense>
    );
  }

  // UI-only preview of the OAuth consent screen renders WITHOUT a session: it
  // shows hardcoded sample data and makes no API calls, so gating it behind
  // login would defeat its purpose (design/QA review). Rendering it to a
  // logged-out visitor is safe because the preview is entirely inert.
  if (isOAuthConsentPreview(location.pathname, location.search)) {
    return <>{children}</>;
  }

  if (isLoadingAuth) {
    return <Loader height="100vh" />;
  }

  // check if we are running on previous domain.
  if (!user && typeof window !== "undefined" && window.location.hostname.indexOf("tether.co.nz") !== -1) {
    const currentUrl = new URL(window.location.href.replace("tether.co.nz", "tetherhq.com"));
    window.location.href = currentUrl.toString();

    return null;
  }

  if (!user || (location.pathname.startsWith("/auth/"))) {
    return <AuthPage />;
  }

  return <>{children}</>;
};
